import { Check, Circle, CircleAlert, Code2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/shared/ui/button';
import { Spinner } from '@/shared/ui/spinner';

import { SLOW_STEP_NOTICE_MS } from '../workspace-progress';

import type { WorkspaceProgressModel } from '../workspace-progress';

export function WorkshopProgress({
  model,
  operationKey,
  onCheckStatus,
}: {
  model: WorkspaceProgressModel;
  operationKey: string;
  onCheckStatus?: () => void;
}) {
  const stage = model.current;
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    setElapsedSeconds(0);
    if (!model.running) return;
    const startedAt = Date.now();
    const timer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [operationKey, stage, model.running]);
  return (
    <div className="workshop-progress">
      <span className="border-line bg-sidebar mb-5 inline-flex rounded-xl border p-3">
        {model.failed ? (
          <CircleAlert className="text-danger size-5" />
        ) : model.running ? (
          <Spinner className="text-accent size-5" />
        ) : (
          <Code2 className="text-fg-muted size-5" />
        )}
      </span>
      <div role="status" aria-live="polite">
        <h3>{model.title}</h3>
        <p>{model.description}</p>
        <span className="sr-only">{model.steps[model.current] ?? ''}</span>
      </div>
      {model.running ? <p aria-live="off">{elapsedSeconds}s on this step</p> : null}
      {model.steps.length > 0 ? (
        <>
          <div
            className="workshop-progress-track"
            role="progressbar"
            aria-label="Workspace preparation"
            aria-valuetext={
              model.failed ? 'Operation needs attention' : (model.steps[model.current] ?? 'Ready')
            }
          >
            {model.steps.map((step, index) => (
              <span
                key={step}
                data-state={
                  index < model.current
                    ? 'complete'
                    : index === model.current && model.running
                      ? 'current'
                      : 'pending'
                }
              />
            ))}
          </div>
          <ol aria-label="Workspace steps">
            {model.steps.map((step, index) => (
              <li
                key={step}
                data-state={
                  index < model.current
                    ? 'complete'
                    : index === model.current
                      ? 'current'
                      : 'pending'
                }
              >
                {index < model.current ? (
                  <Check className="size-4" aria-hidden="true" />
                ) : index === model.current && model.running ? (
                  <Spinner />
                ) : (
                  <Circle className="size-4" aria-hidden="true" />
                )}
                <span>
                  {step}
                  <span className="sr-only">
                    {index < model.current
                      ? ', complete'
                      : index === model.current
                        ? ', current step'
                        : ', pending'}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </>
      ) : null}
      {(model.failed || model.attention) && onCheckStatus ? (
        <Button className="mt-6" variant="secondary" onClick={onCheckStatus}>
          Check status
        </Button>
      ) : null}
      {elapsedSeconds * 1000 >= SLOW_STEP_NOTICE_MS && model.running ? (
        <div className="border-line mt-6 space-y-3 border-t pt-4">
          <p role="status">
            This step is taking longer than usual. We’re still checking its status. Keep this tab
            open so any pending saves can finish.
          </p>
          {onCheckStatus ? (
            <Button variant="secondary" onClick={onCheckStatus}>
              Check status
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
